import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Outlet } from 'react-router-dom';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Chat from './pages/Chat';
import RandomChat from './pages/RandomChat';
import FriendRequests from './pages/FriendRequests';
import Feed from './pages/Feed';
import Profile from './pages/Profile';
import Layout from './components/Layout';
import AdminPanel from './pages/AdminPanel';
import FeedbackPage from './pages/FeedbackPage';
import LandingPage from './pages/LandingPage';
import PrivacyCenter from './pages/PrivacyCenter';

function RequireAuth({ children }) {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" replace />;
}

function RequireNoAuth({ children }) {
  const token = localStorage.getItem('token');
  return token ? <Navigate to="/random" replace /> : children;
}

function RequireAdmin({ children }) {
  const token = localStorage.getItem('token');
  const isStaff = localStorage.getItem('is_staff') === 'true';
  const isSuperuser = localStorage.getItem('is_superuser') === 'true';
  if (!token) return <Navigate to="/login" replace />;
  if (!isStaff && !isSuperuser) return <Navigate to="/random" replace />;
  return children;
}

// OptionalLayout: Shows the app sidebar/header ONLY if logged in.
// This allows LandingPages to be public for SEO but integrated for users.
function OptionalLayout() {
  const token = localStorage.getItem('token');
  if (token) {
    return (
      <RequireAuth>
        <Layout />
      </RequireAuth>
    );
  }
  return <Outlet />;
}

function AndroidBackHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('token');

  useEffect(() => {
    window.history.pushState(null, '', window.location.pathname);
    const onPopState = () => {
      const path = location.pathname;
      if (token && (path === '/login' || path === '/register')) {
        navigate('/random', { replace: true });
        return;
      }
      if (/^\/chat\/.+/.test(path)) {
        navigate('/chat', { replace: true });
      }
      else if (path !== '/random' && path !== '/login' && path !== '/register') {
        if (token) navigate('/random', { replace: true });
      }
      else {
        window.history.pushState(null, '', window.location.pathname);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [location.pathname, navigate, token]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AndroidBackHandler />
      <Routes>
        {/* SEO & CONTENT PAGES (Public by default, Layout-aware if logged in) */}
        <Route element={<OptionalLayout />}>
          <Route path="/random-chat"   element={<LandingPage />} />
          <Route path="/stranger-chat" element={<LandingPage />} />
          <Route path="/online-chat"   element={<LandingPage />} />
          <Route path="/anonymous-chat" element={<LandingPage />} />
          <Route path="/video-chat"    element={<LandingPage />} />
          <Route path="/roblox-chat"   element={<LandingPage />} />
          <Route path="/roblox-community-hangout" element={<LandingPage />} />
          <Route path="/roblox-gamers-chat" element={<LandingPage />} />
          <Route path="/live-random-chat" element={<LandingPage />} />
          <Route path="/global-chat"   element={<LandingPage />} />
          <Route path="/anonymous-messaging" element={<LandingPage />} />
          <Route path="/stranger-talk" element={<LandingPage />} />
          <Route path="/random-people-chat" element={<LandingPage />} />
          <Route path="/privacy-focused-chat" element={<LandingPage />} />
          <Route path="/friend-chat"   element={<LandingPage />} />
          <Route path="/privacy"      element={<PrivacyCenter />} />
        </Route>

        {/* AUTH PAGES */}
        <Route path="/login"    element={<RequireNoAuth><Login /></RequireNoAuth>} />
        <Route path="/register" element={<RequireNoAuth><Register /></RequireNoAuth>} />
        
        {/* ADMIN */}
        <Route path="/admin-panel/*" element={<RequireAdmin><AdminPanel /></RequireAdmin>} />
        
        {/* PROTECTED APP PAGES */}
        <Route path="/" element={<RequireAuth><Layout /></RequireAuth>}>
          <Route index element={<Navigate to="/random" replace />} />
          <Route path="chat"         element={<Chat />} />
          <Route path="chat/:userId" element={<Chat />} />
          <Route path="random"       element={<RandomChat />} />
          <Route path="friends"      element={<FriendRequests />} />
          <Route path="feed"         element={<Feed />} />
          <Route path="profile"      element={<Profile />} />
          <Route path="feedback"     element={<FeedbackPage />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/random" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
